import React, { useState, useRef, useEffect } from 'react';
import { useTranslation, Language } from '../../i18n';
import './LanguageSwitcher.css';
import Icon from '../Icon';

interface LanguageSwitcherProps {
  /** default: on the brand header; promo: marketing header; surface: on light/dark surfaces (menus) */
  variant?: 'default' | 'promo' | 'surface';
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'default' }) => {
  const { language, setLanguage, languages, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLanguageSelect = (langCode: Language) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`language-switcher ${variant === 'promo' ? 'promo-variant' : ''} ${variant === 'surface' ? 'surface-variant' : ''}`} ref={dropdownRef}>
      <button
        className={`language-switcher-button ${variant === 'promo' ? 'promo-button' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
  aria-label={t('common.changeLanguage')}
  title={t('common.changeLanguage')}
      >
        <span className="language-code">{language.toUpperCase()}</span>
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>
      
      {isOpen && (
        <div className="language-dropdown">
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={`language-option ${lang.code === language ? 'active' : ''}`}
              onClick={() => handleLanguageSelect(lang.code)}
            >
              <span className="language-name">{lang.nativeName}</span>
              {lang.code === language && <span className="checkmark"><Icon name="check" /></span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
